import { t } from "i18next";
import { useEffect, useState } from "react";

import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { Checkbox } from "@src/components/ui-components/Checkbox/Checkbox";
import { Stack } from "@src/components/ui-components/Stack/Stack";
import { deletePropertyDocumentationCollectionItem } from "@src/data-hooks/property/collection/propertyCollection.api";
import { usePropertyCollectionFoldersKey, usePropertyCollectionItemsKey } from "@src/data-hooks/property/collection/propertyCollection.service";
import { makeCreatedBy } from "@src/libs/firebase/api.methods";
import { CompanyData } from "@src/to-be-removed/LocalData/Company/CompanyData";
import { isEqual } from "lodash";
import { useMutation, useQueryClient } from "react-query";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { ReadAccessPopup } from "../../../../../components/deprecated/ReadAccessPopup/ReadAccessPopup";
import { NewRightFullHeightPopup } from "../../../../../components/deprecated/RightFullHeight/RightFullHeightPopUp";
import { Text } from "../../../../../components/deprecated/Text/Text";
import { TextInputField } from "../../../../../components/deprecated/TextInputField/TextInputField";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { CompanyCollectionData } from "../../../../../to-be-removed/LocalData/Company/Collections/CompanyCollectionData";
import { CompanyCollectionItemData } from "../../../../../to-be-removed/LocalData/Company/Collections/CompanyCollectionItemData";
import { CompanyCustomFieldData } from "../../../../../to-be-removed/LocalData/Company/Collections/CompanyCustomFieldData";
import { CustomFieldDataType } from "../../../../../to-be-removed/LocalData/Company/Collections/CustomFieldDataType";
import { ReadAccess } from "../../../../../to-be-removed/LocalData/General/AccessData";
import { CollectionLoadedListener } from "../../../../../to-be-removed/LocalData/General/CollectionLoader";
import { DataSource, ValueDataSource } from "../../../../../to-be-removed/LocalData/General/DataSource";
import { DocumentLoadedListener } from "../../../../../to-be-removed/LocalData/General/FirebaseData";
import { LocalData } from "../../../../../to-be-removed/LocalData/General/LocalData";
import { PropertyCollectionData } from "../../../../../to-be-removed/LocalData/Property/Collections/PropertyCollectionData";
import { PropertyCollectionItemData } from "../../../../../to-be-removed/LocalData/Property/Collections/PropertyCollectionItemData";
import { PropertyCustomFieldData } from "../../../../../to-be-removed/LocalData/Property/Collections/PropertyCustomFieldData";
import { PropertyData } from "../../../../../to-be-removed/LocalData/Property/PropertyData";
import { Role } from "../../../../../to-be-removed/LocalData/User/Role";
import { SystemsAndComponentsUtilities } from "../../../../../utils/systemsAndComponentsUtilities";
import { Utilities } from "../../../../../utils/utilities";
import styles from "./CollectionItemSettings.module.css";
import { AriaButton } from "@src/components/ui-components/Buttons/AriaButton/AriaButton";
import { Archive, Delete, Save } from "@icons/untitled_ui_icons";

type CollectionItemSettingsField = {
  customField: PropertyCustomFieldData;
  dataSource: DataSource;
};

type ItemDataType = PropertyCollectionItemData | CompanyCollectionItemData;
type CollectionDataType = PropertyCollectionData | CompanyCollectionData;

const getInitialReadAccess = ({
  item,
  collection,
  defaultReadAccess,
}: {
  item?: ItemDataType;
  collection?: CollectionDataType;
  defaultReadAccess?: ReadAccess;
}): ReadAccess => {
  if (item?.readAccess) {
    return item.readAccess;
  }

  const access: ReadAccess = { roles: [Role.LANDLORD], ...defaultReadAccess };
  if (collection?.parent instanceof PropertyData) {
    const member = collection.parent.members.getData(LocalData.currentUser.id);
    if (member && !member.roles.includes(Role.LANDLORD)) {
      access.contractorGroups = member.contractorGroups;
    }
    if (member && member.roles.includes(Role.RENTER)) {
      access.propertyUnits = member.units;
    }
  }
  return access;
};

type Props = {
  id: string;
  defaultFields?: { [fieldID: string]: any };
  item?: ItemDataType;
  collection?: CollectionDataType;
  defaultReadAccess?: ReadAccess;
  didSave?: (item: ItemDataType) => void;
  folderID?: string;
};

export const CollectionItemSettings = ({ id, defaultFields, item, collection, didSave, folderID, defaultReadAccess }: Props) => {
  const [, setLastUpdate] = useState(new Date());
  const queryClient = useQueryClient();
  const [readAccess, setReadAccess] = useState(getInitialReadAccess({ item, collection, defaultReadAccess }));
  const [writeAccess, setWriteAccess] = useState<ReadAccess>(
    item?.writeAccess ?? getInitialReadAccess({ item, collection, defaultReadAccess }) ?? {}
  );
  const [fields, setFields] = useState<CollectionItemSettingsField[]>([]);
  const [name, setName] = useState(item?.name ?? "");
  const [hasCustomName, setHasCustomName] = useState<boolean>((item?.name.length ?? 0) > 0);
  const [saving, setSaving] = useState(false);

  const [hasCustomWriteAccess, setHasCustomWriteAccess] = useState<boolean>(
    item?.writeAccess && !isEqual(item?.writeAccess, item?.readAccess) ? true : false
  );

  const $deleteCollectionItem = useMutation(deletePropertyDocumentationCollectionItem);

  const visibleFields = fields
    .filter((field) => {
      if (Object.keys(field.customField.requiredFieldValuesToDisplay).length === 0) return true;
      for (const fieldID in field.customField.requiredFieldValuesToDisplay) {
        const otherField = fields.find((otherField) => otherField.customField.id === fieldID);
        if (
          otherField?.dataSource instanceof ValueDataSource &&
          otherField.dataSource.data &&
          field.customField.requiredFieldValuesToDisplay[fieldID].includes(otherField.dataSource.data)
        ) {
          return true;
        }
      }
      return false;
    })
    .sort((a, b) => (collection?.customFieldsOrder.indexOf(a.customField.id) ?? 0) - (collection?.customFieldsOrder.indexOf(b.customField.id) ?? 10));

  const canSave = name.trim().length > 0;
  const collectionData: PropertyCollectionData | CompanyCollectionData | undefined =
    collection ??
    (item?.parent instanceof PropertyCollectionData ? item?.parent : item?.parent instanceof CompanyCollectionData ? item?.parent : undefined);
  const property = collectionData?.parent instanceof PropertyData ? collectionData.parent : undefined;
  const propertyID = property?.id;
  const company = collection?.parent instanceof CompanyData ? collection?.parent : undefined;

  const updateFields = () => {
    const fields: CollectionItemSettingsField[] = [];
    if (collectionData) {
      for (const doc of collectionData.customFields.documents.filter((field) => CustomFieldDataType.FIELD_TYPES.includes(field.dataType))) {
        const existingData = defaultFields?.[doc.id];
        fields.push({
          customField: doc,
          dataSource: Object.keys(item?.fields ?? {}).includes(doc.id) ? new ValueDataSource(item?.fields[doc.id]) : new ValueDataSource(""),
        });
        const source = fields[fields.length - 1].dataSource;
        if (source instanceof ValueDataSource && source.data === undefined) {
          source.data = existingData ?? source.data;
        }
      }
      setFields(fields);
    }
  };

  useEffect(() => {
    const listeners: (
      | DocumentLoadedListener
      | CollectionLoadedListener<PropertyCustomFieldData>
      | CollectionLoadedListener<CompanyCustomFieldData>
    )[] = [];

    if (item) {
      listeners.push(
        item.setupRealtimeListener(() => {
          setName(item?.name ?? "");
          updateFields();
        })
      );
    }

    if (collectionData) {
      listeners.push(
        ...collectionData.setupCustomFieldsListener(() => {
          updateFields();
          for (const field of collectionData.customFields.documents) {
            field.dataType.loadData({ propertyID: propertyID }, () => {
              setLastUpdate(new Date());
            });
          }
        })
      );

      if (collectionData.requiredWriteAccess) {
        setReadAccess(Utilities.mergeReadAccess(item?.readAccess, collectionData.requiredWriteAccess));
        setWriteAccess(Utilities.mergeReadAccess(item?.writeAccess, collectionData.requiredWriteAccess));
      }
    }

    updateFields();
    return () => {
      for (const listener of listeners) {
        listener.removeListener();
      }
    };
  }, [id, collectionData, item]);

  const saveTapped = () => {
    setSaving(true);

    const saveData: { [key: string]: any } = {
      name: name.trim(),
      readAccess: Utilities.convertReadAccess(readAccess),
    };

    const fieldData: { [key: string]: any } = {};
    for (const field of fields) {
      if (field.dataSource instanceof ValueDataSource) {
        const fieldValue = field.dataSource.getFirebaseData();
        if (Object.keys(fieldValue.value).length > 0) {
          if (item) {
            saveData[`fields.${field.customField.id}`] = fieldValue;
          } else {
            fieldData[field.customField.id] = fieldValue;
          }
        }
      }
    }

    const accessToConvert = hasCustomWriteAccess ? writeAccess : readAccess;
    saveData["writeAccess"] = Utilities.convertReadAccess(accessToConvert ?? {});

    if (item) {
      ToastUtilities.presentSavingToast(
        item
          .saveUpdate(saveData)
          .then(() => {
            if (item instanceof PropertyCollectionItemData || item instanceof CompanyCollectionItemData) {
              didSave?.(item);
            }
            Popup.remove(id);
          })
          .catch(() => {
            ToastUtilities.presentError(t("main.errorOccured"));
            setSaving(false);
          })
      );
    } else if (collectionData) {
      saveData["fields"] = fieldData;
      if (folderID) {
        saveData["folderID"] = folderID;
      }
      ToastUtilities.presentSavingToast(
        (collectionData instanceof PropertyCollectionData ? collectionData.items : collectionData.items)
          .createData(saveData)
          .then((item) => {
            if (item instanceof PropertyCollectionItemData || item instanceof CompanyCollectionItemData) {
              didSave?.(item);
            }
            Popup.remove(id);
          })
          .catch(() => {
            ToastUtilities.presentError(t("main.errorOccured"));
            setSaving(false);
          })
      );
    }
  };

  const updateValueInField = (index: number, value: any) => {
    const newFields = [...fields];
    const source = newFields[index].dataSource;
    if (source instanceof ValueDataSource) {
      source.data = value;
      setFields(newFields);
    }
  };

  const editReadAccess = () => {
    Popup.present((popupId) => (
      <ReadAccessPopup
        id={popupId}
        accessGroups={["roles", "contractorGroups", "propertyUnits"]}
        requiredAccess={collectionData?.requiredWriteAccess}
        onSelect={(access) => {
          setReadAccess(access);
          if (!hasCustomWriteAccess) setWriteAccess(access);
        }}
        disableCurrentUserAccessCheck={item instanceof CompanyCollectionItemData}
        context={{ propertyID }}
        readAccess={readAccess}
      />
    ));
  };

  const editWriteAccess = () => {
    Popup.present((popupId) => (
      <ReadAccessPopup
        id={popupId}
        accessGroups={["roles", "contractorGroups", "propertyUnits"]}
        requiredAccess={collectionData?.requiredWriteAccess}
        title={t("main.editWriteAccess")}
        onSelect={(access) => {
          setWriteAccess(access);
        }}
        disableCurrentUserAccessCheck={item instanceof CompanyCollectionItemData}
        context={{ propertyID }}
        readAccess={writeAccess}
      />
    ));
  };

  const deleteTapped = () => {
    if (item) {
      PopupTemplates.presentVerifyDelete(
        item.name,
        () => {
          Popup.remove(id);
          if (item?.parent instanceof PropertyCollectionData) {
            const propertyId = item.parent.parent?.id as string;
            const collectionId = item.parent.id;
            ToastUtilities.presentPromise(
              t("main.deleting"),
              $deleteCollectionItem.mutateAsync({
                propertyId,
                collectionId,
                itemId: item.id,
                deletedBy: makeCreatedBy(),
              })
            ).then(() => {
              void queryClient.invalidateQueries(usePropertyCollectionItemsKey({ collectionId, propertyId }), {
                refetchInactive: true,
              });
              void queryClient.invalidateQueries(usePropertyCollectionFoldersKey({ collectionId, propertyId }), {
                refetchInactive: true,
              });
            });
          }
        },
        t("main.itemDeletionCaution")
      );
    }
  };

  const archiveTapped = () => {
    if (item) {
      if (item.archived) {
        PopupTemplates.presentVerifyAction(`${t("main.unarchiveCollectionItem")}?`, t("main.collectionItemDearchivationCaution"), () => {
          ToastUtilities.presentSavingToast(
            item!.archive().then(() => {
              item!.load();
            })
          );
        });
      } else {
        PopupTemplates.presentVerifyAction(`${t("main.archiveCollectionItem")}?`, t("main.collectionItemArchivationCaution"), () => {
          ToastUtilities.presentSavingToast(item!.archive()).then(() => {
            item!.load();
          });
        });
      }
    }
  };

  return (
    <NewRightFullHeightPopup id={id}>
      <div className={styles.container}>
        <Text size="xl" isBold>
          {item ? t("main.settings") : `${t("main.create")} ${collectionData?.name.translate(1).toLowerCase()}`}
        </Text>

        <ul id={styles.fields}>
          <li key="name">
            <Text className={styles.fieldTitle} size="sm">
              {t("main.name")}
            </Text>
            <TextInputField
              placeholder={t("main.enterName")}
              fullWidth
              value={name}
              onChange={(text) => {
                setName(text);
                setHasCustomName(true);
              }}
            />
          </li>
          {visibleFields.map((field) => {
            const index = fields.indexOf(field);
            return (
              <li key={field.customField.id}>
                <Text className={styles.fieldTitle} size="sm">
                  {field.customField.name.translate() + (field.customField.deprecated ? ` (${t("main.deprecated")})` : "")}
                </Text>
                {field.dataSource instanceof ValueDataSource &&
                  field.customField.dataType.getDataEditor(field.dataSource.data, field.customField.dataOptions, { propertyID, company }, (data) => {
                    updateValueInField(index, data);

                    if (field.customField.dataType === CustomFieldDataType.NORWEGIAN_STANDARDS && data && !hasCustomName) {
                      setName(SystemsAndComponentsUtilities.getOptionWithKey(undefined, data)?.title ?? "");
                    }
                  })}
              </li>
            );
          })}

          <hr />
          <li key="readAccess">
            <Text size="sm" className={styles.fieldTitle}>
              {t("main.visibleFor")}
            </Text>
            <Stack spacing="4px">
              <Text size="md">{Utilities.getReadAccessString(readAccess)}</Text>
              <AriaButton onPress={() => editReadAccess()}>{t("main.edit")}</AriaButton>
            </Stack>
          </li>

          <li key="writeAccess">
            <Text size="sm" className={styles.fieldTitle}>
              {t("main.writeAccess")}
            </Text>
            <Checkbox
              value={!hasCustomWriteAccess}
              description={t("main.useCustomWriteAccess")}
              onChange={() => {
                setHasCustomWriteAccess(!hasCustomWriteAccess);
              }}
            />
            {hasCustomWriteAccess && (
              <Stack spacing="4px" style={{ marginTop: "8px" }}>
                <Text size="md">{Utilities.getReadAccessString(writeAccess)}</Text>
                <AriaButton variant="tertiary" onPress={() => editWriteAccess()}>
                  {t("main.edit")}
                </AriaButton>
              </Stack>
            )}
          </li>
        </ul>

        <div id={styles.bottomBar}>
          <AriaButton fullWidth isLoading={saving} isDisabled={!canSave} onPress={() => saveTapped()}>
            {item ? t("main.save") : t("main.create")}
            <Save />
          </AriaButton>
          {item && (
            <>
              <AriaButton fullWidth style={{ marginTop: 8 }} variant="secondary" onPress={() => archiveTapped()}>
                {item.archived ? t("main.unarchive") : t("main.archive")}
                <Archive />
              </AriaButton>
              <AriaButton fullWidth style={{ marginTop: 8 }} color="destructive" onPress={() => deleteTapped()}>
                {t("main.delete")}
                <Delete />
              </AriaButton>
            </>
          )}
        </div>
      </div>
    </NewRightFullHeightPopup>
  );
};
